import { Rocket } from "lucide-react";
import type { DeployRecordView } from "@/lib/api/pull-requests";
import { shortSha } from "@/components/pull-requests/pull-request-status";

/**
 * ENG-421: the newest deploy of this change — its status, the migrations it
 * applied (or "none"), the backup taken first, and the commit deployed.
 * The API sends records newest first. Renders nothing when the server
 * doesn't send deploy records at all (older servers), and a quiet
 * "no deploys yet" line when the list is empty.
 */
export function DeployRecordSummary({ records }: { records: DeployRecordView[] | undefined }) {
  if (records === undefined) return null;
  const latest = records[0];
  if (!latest) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="deploy-record-empty">
        No deploys recorded yet.
      </p>
    );
  }
  return (
    <div className="space-y-1 text-sm" data-testid="deploy-record">
      <div className="flex items-center gap-2">
        <Rocket className="h-4 w-4 text-muted-foreground" aria-hidden />
        <span>
          Latest deploy: <span className="font-medium" data-testid="deploy-record-status">{latest.status}</span>
          {" "}of <code className="text-xs">{shortSha(latest.commitSha)}</code>
        </span>
      </div>
      <div className="pl-6 text-xs text-muted-foreground space-y-0.5">
        <div data-testid="deploy-record-migrations">
          Migrations applied:{" "}
          {latest.appliedMigrations.length === 0 ? (
            "none"
          ) : (
            <ul className="list-disc pl-5">
              {latest.appliedMigrations.map((m) => (
                <li key={m}>
                  <code>{m}</code>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div data-testid="deploy-record-backup">
          Backup: {latest.backupObject ? <code>{latest.backupObject}</code> : "none"}
        </div>
      </div>
    </div>
  );
}
